import { Component, ViewEncapsulation, OnInit, Input, Output, OnDestroy, ElementRef, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import * as moment from 'moment';
import { forEach, assign } from 'lodash';

import { NumericPipe } from '~shared/pipes';
import { GridHeaderComponent } from '../assorted/grid-header.component';
import { COLUMN_TYPE, GRID_HEIGHT, ColumnDefinition, GridConfig, GridModel } from './Grids';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GridComponent<T> implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() columnDefinitions: Observable<ColumnDefinition[]>;
  @Input() data: Observable<GridModel<T>>;
  @Input() gridOptions: Observable<GridConfig>;
  @Input() height: string = GRID_HEIGHT.AUTO;
  @Input() width;
  @Input() overlayLoadingTemplate;

  @Output() gridReady = new EventEmitter<boolean>(false);

  cellClicked: EventEmitter<any> = new EventEmitter();
  _gridOptions: GridConfig;
  _colDefs: any[];
  _rowData: any[];
  _subscriptions = [];

  domLayout: string;
  fullHeight: string;

  dataSubject: Subject<any> = new Subject();

  DEFAULT_ROW_HEIGHT = 30;
  DEFAULT_HEADER_HEIGHT = 50;

  constructor(public element: ElementRef) { }

  ngOnInit() {
    this.domLayout = this.height === GRID_HEIGHT.AUTO ? 'autoHeight' : '';
    this.fullHeight = this.height === GRID_HEIGHT.AUTO || this.height === GRID_HEIGHT.FULL ? 'h-100' : this.height;

    this._subscriptions.push(this.columnDefinitions.subscribe(col => {
      this._colDefs = this.generateColumnDefinitions(col);
    }));

    this._subscriptions.push(this.gridOptions.subscribe(options => {
      this._gridOptions = this.width === 'overflow'
        ? assign(this.generateGridConfig(options), {
          onGridReady: () => {
            this.gridReady.emit(true);
          }
        })
        : assign(this.generateGridConfig(options), {
          onGridReady: () => setTimeout(() => {
            this._gridOptions && this._gridOptions.api && this._gridOptions.api.sizeColumnsToFit();
            this.gridReady.emit(true);
          }, 500),
          onGridSizeChanged: () => this._gridOptions && this._gridOptions.api && this._gridOptions.api.sizeColumnsToFit()
        });
    }));

    this._subscriptions.push(this.data.subscribe(r => {
      if (this._gridOptions && this._gridOptions.api) {
        this._gridOptions.api.setRowData(r.data);

        this._gridOptions.api.setFloatingBottomRowData(
          r.average || r.difference
            ? [r.average, r.difference].filter(f => !!f)
            : null);
      }
      else {
        this._rowData = r.data;
      }
    }));
  }

  ngAfterViewInit() {
    this._gridOptions.api.showLoadingOverlay();
  }

  showLoadingIcon() {
    this._gridOptions.api.setRowData([]);
    this._gridOptions.api.showLoadingOverlay();
  }

  ngOnDestroy() {
    forEach(this._subscriptions, (v) => v.unsubscribe());
    this._subscriptions = [];
    this.gridReady.complete();
    this.dataSubject.complete();
    this.cellClicked.complete();
  }

  private onCellClicked($event) {
    if ($event.colDef.suppressClickEvent) {
      $event.node.selectThisNode();
      return;
    }

    this.cellClicked.emit($event);
  }

  private generateGridConfig(options: GridConfig): GridConfig {
    return {
      suppressMovableColumns: options.suppressMovableColumns || true,
      context: options.context || null,
      getRowNodeId: options.getRowNodeId || null,
      defaultColDef: {
        headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent
      },
      rowClass: options.rowClass || null,
      headerHeight: options.headerHeight || 25
    };
  }

  private generateColumnDefinitions(columns: ColumnDefinition[]): any[] {
    let agGridColumns = [];

    forEach(columns, (col) => {
      let column;
      if (col && col.columns) { // grouped columns
        column = {
          headerName: col.label,
          headerClass: 'text-center',
          children: []
        };
        forEach(col.columns, (child: any, idx: any) => {
          column.children.push({
            headerName: child.label,
            field: child.property,
            // width: child.width,
            unSortIcon: true,
            suppressSorting: !child.sortable || false,
            suppressClickEvent: child.clickEventSuppressed || false,
            valueFormatter: child.valueFormatter,
            headerClass: child.headerClass + ' sub-header-' + (parseInt(idx) % 2 === 0 ? 'even' : 'odd') + (child.sortable ? ' pointer' : '') || 'sub-header-' + (parseInt(idx) % 2 === 0 ? 'even' : 'odd') + (child.sortable ? ' pointer' : ''),
            cellClass: child.cellClass || 'text-center',
            cellRenderer: child.cellRenderer || this.cellRenderer,
            cellRendererFramework: col.cellRendererFramework || null,
            comparator: child.comparator || null,
            type: child.type,
            format: child.format,
            noData: child.noData,
            decimals: child.decimals
          });
        });
      } else {
        column = {
          headerName: col.label,
          headerClass: col.headerClass,
          field: col.property,
          // width: col.width,
          pinned: col.pinned || false,
          suppressSorting: !col.sortable || false,
          suppressClickEvent: col.clickEventSuppressed || false,
          cellClass: col.cellClass || 'text-center',
          cellRenderer: col.cellRenderer || this.cellRenderer,
          cellRendererFramework: col.cellRendererFramework,
          comparator: col.comparator || null,
          type: col.type,
          format: col.format,
          noData: col.noData,
          decimals: col.decimals,
          hide: col.hidden
        };
      }

      agGridColumns.push(column);
    });

    return agGridColumns;
  }

  private cellRenderer(params) {
    let noData = params.colDef.noData || 'N/A';
    switch (params.colDef.type) {
      case COLUMN_TYPE.STRING:
        return params.value ? params.value : noData;
      case COLUMN_TYPE.CURRENCY:
        return isNaN(parseInt(params.value)) ? noData : new NumericPipe().transform(params.value, COLUMN_TYPE.CURRENCY, params.colDef.decimals);
      case COLUMN_TYPE.DECIMAL:
        return isNaN(parseInt(params.value)) ? noData : new NumericPipe().transform(params.value, COLUMN_TYPE.DECIMAL, params.colDef.decimals);
      case COLUMN_TYPE.PERCENTAGE:
        return isNaN(parseInt(params.value)) ? noData : new NumericPipe().transform(params.value, COLUMN_TYPE.PERCENTAGE, params.colDef.decimals);
      case COLUMN_TYPE.THOUSAND:
        return isNaN(parseInt(params.value)) ? noData : new NumericPipe().transform(params.value, COLUMN_TYPE.THOUSAND, params.colDef.decimals);
      case COLUMN_TYPE.NUMBER:
        return (isNaN(parseInt(params.value)) && params.value !== 'S') ? noData : (params.value === 'S') ? params.value : parseInt(params.value);
      case COLUMN_TYPE.DATE:
        return params.value ? moment(params.value).format(params.colDef.format) : noData;
      default:
        return params.valueFormatted ? params.valueFormatted : (params.value ? params.value : noData);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/grids/grid.component.ts